import React, { useState } from 'react';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import CameraIcon from '@mui/icons-material/Camera';
import Paper from '@mui/material/Paper';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useAuth } from '../auth/auth_provider.jsx';
import { headerData } from '../data/headerCostum';
import Logo from '../assets/image/logo.png';
import { useNavigate } from 'react-router-dom';

function Copyright(props) {
    return (
        <Typography variant="body2" color="darkslategray" align="center" {...props}>
            {'Copyright © '}
            <Link color="inherit" href="https://diskominfo.makassarkota.go.id/">
                Diskominfo Kota Makassar
            </Link>{' '}
            {new Date().getFullYear()}
            {'.'}
        </Typography>
    );
}

const theme = createTheme({
    palette: {
        primary: {
            main: '#1dc39f', // Green theme untuk PIC
        },
        secondary: {
            main: '#8ff9ab',
        },
    },
    typography: {
        h5: {
            fontWeight: 600,
        },
    },
});

const HalLoginPIC = () => {
    const { loginPIC } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        username: '',
        password: ''
    });
    const navigate = useNavigate();

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (error) setError('');
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const validateForm = () => {
        if (!formData.username.trim()) {
            setError('Username harus diisi');
            return false;
        }
        if (!formData.password) {
            setError('Password harus diisi');
            return false;
        }
        if (formData.username.length < 3) {
            setError('Username minimal 3 karakter');
            return false;
        }
        if (formData.password.length < 4) {
            setError('Password minimal 4 karakter');
            return false;
        }
        return true;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}api/pic/login`, {
                method: 'POST',
                headers: headerData,
                body: JSON.stringify({
                    username: formData.username.trim(),
                    password: formData.password,
                }),
            });
            
            const result = await response.json();

            if (response.ok && result.token) {
                loginPIC(result);
                console.log(result);
            } else {
                setError(result.message || 'Login gagal. Periksa username dan password Anda.');
            }
        } catch (err) {
            console.error('Login error:', err);
            setError('Terjadi kesalahan koneksi. Silakan coba lagi.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #1dc39f 0%, #8ff9ab 100%)',
                py: 3
            }}
        >
            <ThemeProvider theme={theme}>
                <Container component="main" maxWidth="xs">
                    <CssBaseline />
                    <Paper
                        elevation={10}
                        sx={{
                            px: 3,
                            py: 4,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            borderRadius: 3,
                            background: 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(10px)',
                            maxWidth: 400,
                            mx: 'auto'
                        }}
                    >
                        {/* Logo */}
                        <Box sx={{ mb: 2, maxWidth: 120 }}>
                            <img src={Logo} alt="Main Logo" width="100%" style={{ borderRadius: 8 }} />
                        </Box>
                        
                        <Typography component="h1" variant="h5" sx={{ mb: 1, color: 'primary.main', fontWeight: 700 }}>
                            CCTV LONGWIS v2
                        </Typography>
                        
                        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', textAlign: 'center' }}>
                            Login PIC - Penanggung Jawab
                        </Typography>

                        {error && (
                            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                id="username"
                                label="Username"
                                name="username"
                                autoComplete="username"
                                autoFocus
                                value={formData.username}
                                onChange={handleInputChange}
                                error={error && !formData.username.trim()}
                                disabled={loading}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <PersonIcon color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ mb: 1.5 }}
                            />
                            
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="password"
                                label="Password"
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                autoComplete="current-password"
                                value={formData.password}
                                onChange={handleInputChange}
                                error={error && !formData.password}
                                disabled={loading}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LockIcon color="action" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={handleClickShowPassword}
                                                edge="end"
                                                disabled={loading}
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ mb: 2.5 }}
                            />
                            
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                disabled={loading}
                                sx={{
                                    py: 1.2,
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    borderRadius: 2,
                                    background: 'linear-gradient(90deg, #1dc39f, #8ff9ab)',
                                    textTransform: 'none',
                                    mb: 2,
                                    '&:hover': {
                                        background: 'linear-gradient(90deg, #17a085, #7ce89a)',
                                    }
                                }}
                            >
                                {loading ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CircularProgress size={20} color="inherit" />
                                        Masuk...
                                    </Box>
                                ) : (
                                    'Masuk PIC'
                                )}
                            </Button>

                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => navigate('cekcctv')}
                                disabled={loading}
                                startIcon={<CameraIcon />}
                                sx={{
                                    py: 1.2,
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    borderColor: 'primary.main',
                                    color: 'primary.main',
                                    '&:hover': {
                                        borderColor: 'primary.dark',
                                        backgroundColor: 'rgba(29, 195, 159, 0.1)',
                                    }
                                }}
                            >
                                Cek CCTV
                            </Button>
                        </Box>
                        
                        <Copyright sx={{ mt: 3 }} />
                    </Paper>
                </Container>
            </ThemeProvider>
        </Box>
    );
};

export default HalLoginPIC;
